import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onBeforeUnmount, nextTick, markRaw } from 'vue';
import { formatExternalTracker } from '@/helpers/impression-tracker-helper';
import { ImaSdkLoader } from '../helpers/ima-sdk-loader';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import type { PreRollVideoAdUnit } from '@/components/experiments/PreRollAndStaticBBAdsExp/components/constants';

type AdState = 'waitingForViewport' | 'loadingSdk' | 'requestingAd' | 'playing' | 'completed' | 'fallback';


export default /*#__PURE__*/_defineComponent({
  __name: 'PreRollVideoAd',
  props: {
    adUnit: null,
    listSwiper: { type: Boolean }
  },
  emits: ["adFailed"],
  setup(__props: any, { emit }: { emit: ({ (e: 'adFailed'): void }), expose: any, slots: any, attrs: any }) {

const props = __props as {
	adUnit: PreRollVideoAdUnit;
	listSwiper?: boolean;
};





const VAST_TAG_BASE =
	'https://pubads.g.doubleclick.net/gampad/ads?iu=/21679119891/[AD_UNIT]&description_url=https%3A%2F%2Fwww.justwatch.com%2F&tfcd=0&npa=0&sz=640x360%7C1280x720%7C1920x1080%7C3840x2160&gdfp_req=1&unviewed_position_start=1&output=xml_vast4&env=vp&correlator=[timestamp]&pmnd=6000&pmxd=30000&pmad=3&gdpr=${GDPR}&gdpr_consent=${GDPR_CONSENT_755}&gdpr_pd=${GDPR_PD}&ifa_type=web&frm=0&vis=1&sdr=1&is_amp=0&ctv=0&mpt=ima-sdk&sdki=100000445&ptt=20&vo=0&sdk_apis=2,7,8&osd=2&htps=10&nel=1';

const MAX_AD_RETRIES = 3;

const adState = ref<AdState>('waitingForViewport');
const retryCount = ref(0);

const hasRequestedAd = ref(false);
const { webLocale, language } = storeToRefs(useLanguageStore());
const { activeRoute } = storeToRefs(useRoutingStore());

const sessionId = `${Date.now().toString(16).toUpperCase()}-${Math.random()
	.toString(16)
	.substring(2, 10)
	.toUpperCase()}`;

// Dynamic VAST tag parameters
const currentPageUrl = computed(() => {
	const path = activeRoute.value?.path || '/';
	const fullUrl = `https://www.justwatch.com${path}`;
	return encodeURIComponent(fullUrl);
});

const sdkVersion = computed(() => {
	const version = window.google?.ima?.VERSION || '3.744.0';
	return `h.${version}`;
});

// Build complete VAST tag URL with dynamic parameters only - trying to attach as many as possible to match bitmovin's request and maximize targeting
const formattedVastTagUrl = computed(() => {
	const cacheBuster = Date.now();
	const dynamicParams = buildUrlParams({
		cb: cacheBuster,
		hl: language.value,
		sdkv: sdkVersion.value,
		sid: sessionId,
		url: currentPageUrl.value,
		dt: cacheBuster,
	});

	const vastTagWithParams = `${VAST_TAG_BASE.replace('[AD_UNIT]', props.adUnit)}&${dynamicParams}`;

	return formatExternalTracker({
		tracker: vastTagWithParams,
		webLocale: webLocale.value,
	});
});

const videoElement = ref<HTMLVideoElement | null>(null);
const adContainer = ref<HTMLDivElement | null>(null);

const adDisplayContainer = ref<google.ima.AdDisplayContainer | null>(null);
const adsLoader = ref<google.ima.AdsLoader | null>(null);
const adsManager = ref<google.ima.AdsManager | null>(null);

const showVideoAdSlot = computed(() => {
	return !['fallback', 'completed'].includes(adState.value);
});

const isLoading = computed(() => ['loadingSdk', 'requestingAd'].includes(adState.value));

function transitionTo(newState: AdState, error?: Error) {
	adState.value = newState;

	if (error) {
		console.error(`[PreRollVideoAd] Transitioned to state: ${newState}`, error || '');
	}

	if (newState === 'fallback') {
		emit('adFailed');
		cleanup();
	}
}

const observeVisibilityOptions = computed(() => ({
	callback: (isVisible: boolean) => {
		if (isVisible && adState.value === 'waitingForViewport' && !hasRequestedAd.value) {
			hasRequestedAd.value = true;
			transitionTo('loadingSdk');
			loadSdkAndInitialize();
		}
	},
	once: true,
	throttle: 300,
	intersection: {
		root: null,
		rootMargin: `100px`,
		threshold: 0,
	},
}));

async function loadSdkAndInitialize() {
	try {
		await ImaSdkLoader.loadSdk();

		transitionTo('requestingAd');

		await nextTick();
		initializeImaAndRequestAd();
	} catch (error) {
		transitionTo('fallback', error as Error);
	}
}

function initializeImaAndRequestAd() {
	if (!videoElement.value || !adContainer.value) {
		transitionTo('fallback', new Error('DOM refs not available'));
		return;
	}

	if (!window.google?.ima) {
		transitionTo('fallback', new Error('Google IMA SDK not loaded'));
		return;
	}

	try {
		// Get unwrapped DOM elements (not Vue refs)
		const videoEl = videoElement.value;
		const adContainerEl = adContainer.value;

		if (!videoEl || !adContainerEl) {
			transitionTo('fallback', new Error('DOM elements not available'));
			return;
		}

		// Verify we have actual DOM elements
		if (!(videoEl instanceof HTMLVideoElement)) {
			transitionTo('fallback', new Error('Invalid video element'));
			return;
		}

		if (!(adContainerEl instanceof HTMLElement)) {
			transitionTo('fallback', new Error('Invalid ad container element'));
			return;
		}

		// Create ad display container with unwrapped elements
		// Use markRaw to prevent Vue from making these objects reactive
		const container = new window.google.ima.AdDisplayContainer(adContainerEl, videoEl);
		adDisplayContainer.value = markRaw(container);

		// Create ads loader
		const loader = new window.google.ima.AdsLoader(adDisplayContainer.value);
		adsLoader.value = markRaw(loader);

		// Add event listeners
		adsLoader.value.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, onAdError, false);

		adsLoader.value.addEventListener(
			window.google.ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
			onAdsManagerLoaded,
			false
		);

		// Initialize ad display container
		adDisplayContainer.value.initialize();

		// Request ads
		fireAdsRequest(adContainerEl.offsetWidth, adContainerEl.offsetHeight);
	} catch (error) {
		transitionTo('fallback', error as Error);
	}
}

function fireAdsRequest(width: number, height: number) {
	if (!adsLoader.value || !window?.google?.ima) return;

	const adsRequest = new window.google.ima.AdsRequest();
	adsRequest.adTagUrl = formattedVastTagUrl.value;
	adsRequest.linearAdSlotWidth = width;
	adsRequest.linearAdSlotHeight = height;
	adsRequest.nonLinearAdSlotWidth = 0;
	adsRequest.nonLinearAdSlotHeight = 0;
	adsLoader.value.requestAds(adsRequest);
}

function onAdsManagerLoaded(event: google.ima.AdsManagerLoadedEvent) {
	if (!window.google?.ima) return;

	try {
		const videoEl = videoElement.value;
		if (!videoEl) {
			transitionTo('fallback', new Error('Video element not available'));
			return;
		}

		// Use markRaw to prevent Vue reactivity on the ads manager
		const manager = event.getAdsManager(videoEl);
		adsManager.value = markRaw(manager);

		adsManager.value.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, onAdError);

		adsManager.value.addEventListener(window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED, requestNextAd);

		const width = adContainer.value!.offsetWidth;
		const height = adContainer.value!.offsetHeight;
		adsManager.value.init(width, height, window.google.ima.ViewMode.NORMAL);
		adsManager.value.start();

		transitionTo('playing');
	} catch (error) {
		transitionTo('fallback', error as Error);
	}
}

function onAdError(adErrorEvent: google.ima.AdErrorEvent) {
	const error = adErrorEvent.getError();

	if (adState.value === 'requestingAd' && retryCount.value < MAX_AD_RETRIES && adsLoader.value) {
		retryCount.value++;
		setTimeout(() => {
			fireAdsRequest(adContainer.value?.offsetWidth ?? 0, adContainer.value?.offsetHeight ?? 0);
		}, 1000);
		return;
	}

	transitionTo('fallback', new Error(error?.getMessage() || 'IMA ad error'));
}

async function requestNextAd() {
	// Clean up previous ad manager and loader
	try {
		if (adsManager.value) {
			adsManager.value.destroy();
			adsManager.value = null;
		}

		if (adsLoader.value) {
			adsLoader.value.destroy();
			adsLoader.value = null;
		}

		adDisplayContainer.value = null;
	} catch (error) {
		console.error('[PreRollVideoAd] Error during cleanup before next ad:', error);
	}

	// Transition to requesting state
	transitionTo('requestingAd');

	// Wait a tick for state to update
	await nextTick();

	// Request new ad
	initializeImaAndRequestAd();
}

function cleanup() {
	try {
		if (adsManager.value) {
			adsManager.value.destroy();
			adsManager.value = null;
		}

		if (adsLoader.value) {
			adsLoader.value.destroy();
			adsLoader.value = null;
		}

		adDisplayContainer.value = null;
	} catch (error) {
		console.error('[PreRollVideoAd] Error during cleanup:', error);
	}
}

function buildUrlParams(params: Record<string, string | number>): string {
	return Object.entries(params)
		.map(([key, value]) => `${key}=${value}`)
		.join('&');
}

onBeforeUnmount(() => {
	cleanup();
});

return { __sfc: true,emit, props, VAST_TAG_BASE, MAX_AD_RETRIES, adState, retryCount, hasRequestedAd, webLocale, language, activeRoute, sessionId, currentPageUrl, sdkVersion, formattedVastTagUrl, videoElement, adContainer, adDisplayContainer, adsLoader, adsManager, showVideoAdSlot, isLoading, transitionTo, observeVisibilityOptions, loadSdkAndInitialize, initializeImaAndRequestAd, fireAdsRequest, onAdsManagerLoaded, onAdError, requestNextAd, cleanup, buildUrlParams }
}

})